import { supabase } from '../supabaseClient';
import { sendEmail, complianceAlertOnBriefAddEmail } from './email';

// Fan out a compliance-alert email to every internal user who has opted
// in via Admin > Internal Users, when a supplier with compliance_status
// of 'amber' or 'red' is added to a brief's recipient list. Best-effort:
// if any part of the lookup or send fails the calling flow still
// completes — we don't want the add-supplier action to roll back just
// because an alert email couldn't go out.
//
// Logs a single row per fan-out to supplier_email_log so the alert
// surfaces in the supplier's Activity tab ("Compliance alert email sent
// — admins notified").
export async function alertAdminsIfNonCompliantOnBriefAdd({ supplierId, briefId, addedByUserId }) {
  if (!supplierId || !briefId) return;

  const { data: supplier } = await supabase
    .from('suppliers')
    .select('id, supplier_name, compliance_status, is_record_only')
    .eq('id', supplierId)
    .maybeSingle();
  if (!supplier) return;
  // Record-only suppliers shouldn't be in a brief in the first place,
  // but if they are, don't fan out an alert — they're a reference
  // record, not an active engagement.
  if (supplier.is_record_only) return;
  if (supplier.compliance_status !== 'amber' && supplier.compliance_status !== 'red') return;

  const { data: brief } = await supabase
    .from('briefs')
    .select('id, brief_name, fusion_project_number, client, fusion_project_name')
    .eq('id', briefId)
    .maybeSingle();
  if (!brief) return;

  let addedByName = '';
  if (addedByUserId) {
    const { data: addedBy } = await supabase
      .from('internal_users')
      .select('full_name')
      .eq('id', addedByUserId)
      .maybeSingle();
    addedByName = addedBy?.full_name || '';
  }

  const { data: recipients } = await supabase
    .from('internal_users')
    .select('id, full_name, email, is_active, compliance_alert_emails')
    .eq('compliance_alert_emails', true);
  const eligible = (recipients || []).filter(r => r.email && r.is_active !== false);
  if (eligible.length === 0) return;

  const briefUrl = `${window.location.origin}/briefs/${brief.id}`;
  const sentTo = [];
  for (const r of eligible) {
    const { subject, html } = complianceAlertOnBriefAddEmail({
      recipientName: r.full_name,
      supplierName: supplier.supplier_name,
      complianceStatus: supplier.compliance_status,
      briefName: brief.brief_name,
      projectNumber: brief.fusion_project_number,
      client: brief.client,
      projectName: brief.fusion_project_name,
      addedByName,
      briefUrl,
    });
    try {
      await sendEmail({ to: r.email, subject, html });
      sentTo.push(r.email);
    } catch (e) {
      // Per-recipient failure shouldn't block the rest of the fan-out
      // or the calling action. Swallow and continue.
    }
  }

  if (sentTo.length > 0) {
    await supabase.from('supplier_email_log').insert([{
      supplier_id: supplier.id,
      email_kind: 'compliance_alert_brief_add',
      recipients: sentTo,
      sent_by: addedByUserId || null,
    }]);
  }
}
